// P9 — schemaVersion-2 Automation builder helpers (spec §14.3–14.7, §15.3–15.5).
// Pure functions only — no supabase/React imports — so the playwright contract
// spec can exercise them without a browser, server, or auth (same pattern as
// src/utils/followUpPaths.js + tests/playwright/follow-up/paths.spec.js).

// ─── Trigger labels / groups ─────────────────────────────────────────────────
// Display metadata for the 21 AutomationTriggerCatalog triggerTypes. The
// catalog row is the source of truth for family/sourceParam/personOptional/
// inputNamespace/inputSpec; this map only provides copy + grouping.

export const TRIGGER_TYPE_META = {
    MANUAL: { label: 'Manual Run', description: 'Start runs yourself from the automation detail screen, optionally with declared inputs.' },
    PATH_ACTION: { label: 'Called by Another Automation', description: 'Started by an Invoke Automation step in another automation, passing declared inputs.' },
    PERSON_CREATED: { label: 'Person Created', description: 'Runs when a new person is created in this organization.' },
    FORM_ENTRY: { label: 'Form Entry', description: 'Runs when the configured form is submitted. Form answers are available as variables.' },
    EVENT_CHECK_IN: { label: 'Event Check In', description: 'Runs when a person checks in to a linked event instance.' },
    ATTENDANCE: { label: 'Attendance Trend', description: 'Runs on missed-week or attendance-milestone trends.' },
    TRAINING_COMPLETED: { label: 'Training Completed', description: 'Runs when a person completes a training module.' },
    SMALLGROUP_COMPLETED: { label: 'Small Group Completed', description: 'Runs when a person completes a small group as a member.' },
    SCHEDULE: { label: 'Scheduled', description: 'Runs once per scheduled occurrence. No person is required.' },
    FOLLOW_UP_ATTENTION_ENTERED: { label: 'Attention Entered', description: 'Runs when a person enters a Follow Up attention state.' },
    FOLLOW_UP_ENROLLMENT_STARTED: { label: 'Follow Up Enrollment Started', description: 'Runs when a Follow Up path enrollment starts.' },
    FOLLOW_UP_ENROLLMENT_COMPLETED: { label: 'Follow Up Enrollment Completed', description: 'Runs when a Follow Up path enrollment completes.' },
    FOLLOW_UP_ENROLLMENT_CANCELLED: { label: 'Follow Up Enrollment Cancelled', description: 'Runs when a Follow Up path enrollment is cancelled.' },
    FOLLOW_UP_CARE_STARTED: { label: 'Care Started', description: 'Runs when a care episode starts for a person.' },
    FOLLOW_UP_CARE_CLOSED: { label: 'Care Closed', description: 'Runs when a care episode closes.' },
    FOLLOW_UP_TOUCH_RECORDED: { label: 'Follow Up Touch Recorded', description: 'Runs when a touch is logged in Follow Up.' },
    TASK_COMPLETED: { label: 'Task Completed', description: 'Runs when a task instance is completed.' },
    TASK_CANCELLED: { label: 'Task Cancelled', description: 'Runs when a task instance is cancelled.' },
};

// Display groups (spec §6.3): People, Forms, Events, Training/Groups,
// Follow Up, Scheduled, Manual/Called, plus Tasks. Maps catalog `family`
// values onto the grouping the picker presents.
export const TRIGGER_FAMILY_GROUPS = [
    { key: 'PEOPLE', label: 'People', families: ['PEOPLE'] },
    { key: 'FORMS', label: 'Forms', families: ['FORMS'] },
    { key: 'EVENTS', label: 'Events', families: ['EVENTS'] },
    { key: 'TRAINING_GROUPS', label: 'Training & Groups', families: ['TRAINING', 'GROUPS'] },
    { key: 'SCHEDULED', label: 'Scheduled', families: ['SCHEDULED'] },
    { key: 'MANUAL', label: 'Manual & Called', families: ['MANUAL', 'AUTOMATIONS'] },
    { key: 'FOLLOW_UP', label: 'Follow Up', families: ['FOLLOW_UP'] },
    { key: 'TASKS', label: 'Tasks', families: ['TASKS'] },
];

export const groupTriggersByFamily = (catalogRows = []) => {
    const active = catalogRows.filter(r => r && r.isActive !== false);
    return TRIGGER_FAMILY_GROUPS
        .map(group => ({
            ...group,
            triggers: active
                .filter(r => group.families.includes(r.family))
                .map(r => ({ ...r, label: TRIGGER_TYPE_META[r.triggerType]?.label || r.triggerType, description: TRIGGER_TYPE_META[r.triggerType]?.description || '' })),
        }))
        .filter(group => group.triggers.length > 0);
};

export const triggerLabel = (triggerType) => TRIGGER_TYPE_META[triggerType]?.label || triggerType || 'Manual';

// ─── Configured-source scope rules (mirror of automation_validate_bindings) ──
// Which optional triggerConfig scope keys are legal per trigger type. The
// server is authoritative — this lets the UI only offer scopes it will accept.

// §25.12: PAUSED/RESUMED/STAGE_CHANGED are retired (inactive in the
// trigger catalog) and Care is episode/person-scoped — no Path binding.
const FOLLOW_UP_PATH_SCOPED_TRIGGERS = new Set([
    'FOLLOW_UP_ENROLLMENT_STARTED', 'FOLLOW_UP_ENROLLMENT_COMPLETED',
    'FOLLOW_UP_ENROLLMENT_CANCELLED', 'FOLLOW_UP_TOUCH_RECORDED',
]);

const FOLLOW_UP_AUDIENCE_SCOPED_TRIGGERS = new Set([
    'FOLLOW_UP_ATTENTION_ENTERED', ...FOLLOW_UP_PATH_SCOPED_TRIGGERS,
    'FOLLOW_UP_CARE_STARTED', 'FOLLOW_UP_CARE_CLOSED',
]);

const TASK_SCOPED_TRIGGERS = new Set(['TASK_COMPLETED', 'TASK_CANCELLED']);

export const triggerAllowsScope = (triggerType, scopeKey) => {
    switch (scopeKey) {
        case 'followUpPathID': return FOLLOW_UP_PATH_SCOPED_TRIGGERS.has(triggerType);
        case 'followUpAudienceID': return FOLLOW_UP_AUDIENCE_SCOPED_TRIGGERS.has(triggerType);
        case 'taskID': return TASK_SCOPED_TRIGGERS.has(triggerType);
        case 'roleID': return triggerType === 'ATTENDANCE';
        default: return false;
    }
};

// Build the triggerConfig object for a trigger step, keeping only keys that
// are legal for the trigger type (sourceParam from the catalog row plus the
// scope map above). Empty values are dropped so they save as "unscoped".
export const buildTriggerConfig = (triggerType, catalogRow, source = {}) => {
    const config = {};
    const sourceParam = catalogRow?.sourceParam || null;
    if (sourceParam && source[sourceParam]) config[sourceParam] = source[sourceParam];
    for (const key of ['followUpPathID', 'followUpAudienceID', 'taskID', 'roleID']) {
        if (source[key] && triggerAllowsScope(triggerType, key)) config[key] = source[key];
    }
    // ATTENDANCE keeps its legacy mode/weeks knobs (same keys the v1 UI writes).
    if (triggerType === 'ATTENDANCE') {
        if (source.mode) config.mode = source.mode;
        if (source.weeks) config.weeks = source.weeks;
    }
    return config;
};

// A catalog row "needs a source" when sourceParam is set (FORM_ENTRY →
// triggerConfig.formID); SCHEDULE needs a PathRecurrence row (warned, not
// hard-blocked, server-side — the UI requires recurrence fields anyway).
export const triggerRequiresConfiguredSource = (catalogRow) => !!catalogRow?.sourceParam;

// ─── New v2 action types ─────────────────────────────────────────────────────

export const V2_ACTION_TYPES = [
    'RESOLVE_PERSON', 'UPSERT_PERSON', 'CREATE_TASK',
    'WAIT_FOR_TASK', 'START_FOLLOW_UP_PATH', 'INVOKE_AUTOMATION',
    'REQUEST_APPROVAL',
];

export const V2_ACTION_TYPE_SET = new Set(V2_ACTION_TYPES);

export const V2_ACTION_TYPE_OPTIONS = [
    { value: 'RESOLVE_PERSON', label: 'Resolve Person' },
    { value: 'UPSERT_PERSON', label: 'Create or Update Person' },
    { value: 'CREATE_TASK', label: 'Create Task' },
    { value: 'WAIT_FOR_TASK', label: 'Wait for Task' },
    { value: 'START_FOLLOW_UP_PATH', label: 'Start Follow Up Path' },
    { value: 'INVOKE_AUTOMATION', label: 'Invoke Automation' },
    { value: 'REQUEST_APPROVAL', label: 'Request Approval' },
];

export const V2_ACTION_TYPE_LABELS = Object.fromEntries(
    V2_ACTION_TYPE_OPTIONS.map(o => [o.value, o.label]));

// Legacy action types that run on v2 definitions with explicit targets.
// v1 configs keep the implicit-enrollee behavior byte-identical; on v2 the
// worker requires config.<targetKey> (see ACTION_PERSON_INPUTS) and never
// falls back to the run person unless compatibilityMode is LEGACY_PERSON.
export const V2_LEGACY_ACTION_TYPES = [
    'SEND_EMAIL', 'SEND_SMS', 'ENROLL_TRAINING', 'ADD_TO_SMALL_GROUP',
    'ADD_TO_ROLE', 'INVITE_TO_REACH', 'UPDATE_PERSON', 'POST_TO_BOARD',
    'CHECK_INTO_EVENT', 'REGISTER_FOR_EVENT',
];
export const V2_LEGACY_ACTION_SET = new Set(V2_LEGACY_ACTION_TYPES);

// ─── Action-type dropdown metadata (sections + per-option subtitles) ─────────
// One table shared by v1 and v2 action-type pickers so the grouped SelectInput
// (SelectInput {section}/{separator}/{subtitle} entries) stays consistent
// across schema versions. Insertion order within each section is display order.
export const ACTION_TYPE_META = {
    SEND_EMAIL:           { label: 'Send Email', section: 'Communication', subtitle: 'Send an email template to the chosen people.' },
    SEND_SMS:             { label: 'Send SMS', section: 'Communication', subtitle: 'Send a text message to the chosen people.' },
    POST_TO_BOARD:        { label: 'Post to a Board', section: 'Communication', subtitle: 'Create a post on a Communication board.' },

    ADD_TO_ROLE:          { label: 'Add to a Role', section: 'People', subtitle: 'Add the chosen people to a role.' },
    ADD_TO_SMALL_GROUP:   { label: 'Add to Small Group', section: 'People', subtitle: 'Enroll the chosen people in a small group.' },
    UPDATE_PERSON:        { label: 'Update Person', section: 'People', subtitle: 'Write fields onto a person\u2019s record.' },
    INVITE_TO_REACH:      { label: 'Invite Person to Reach', section: 'People', subtitle: 'Send Reach invitations to the chosen people.' },
    RESOLVE_PERSON:       { label: 'Resolve Person', section: 'People', subtitle: 'Find an existing person from name, email, or phone.' },
    UPSERT_PERSON:        { label: 'Create or Update Person', section: 'People', subtitle: 'Match a person, then update their record or create a new one.' },

    CHECK_INTO_EVENT:     { label: 'Check Into Event', section: 'Events & Training', subtitle: 'Check the chosen people into an event instance.' },
    REGISTER_FOR_EVENT:   { label: 'Register for Event', section: 'Events & Training', subtitle: 'Register people for an event instance.' },
    ENROLL_TRAINING:      { label: 'Enroll in Training Module', section: 'Events & Training', subtitle: 'Enroll the chosen people in a training module.' },

    CREATE_TASK:          { label: 'Create Task', section: 'Tasks & Approval', subtitle: 'Create a task and record its Task Instance ID as an output.' },
    WAIT_FOR_TASK:        { label: 'Wait for Task', section: 'Tasks & Approval', subtitle: 'Pause the run until a task finishes or a deadline passes.' },
    REQUEST_APPROVAL:     { label: 'Request Approval', section: 'Tasks & Approval', subtitle: 'Ask approvers to approve or deny — the run branches on the decision.' },

    START_FOLLOW_UP_PATH: { label: 'Start Follow Up Path', section: 'Follow Up', subtitle: 'Start a person on a Follow Up path.' },

    INVOKE_AUTOMATION:    { label: 'Invoke Automation', section: 'Automations', subtitle: 'Start a run of another automation, passing its declared inputs.' },
    TRIGGER_PATH:         { label: 'Trigger an Automation', section: 'Automations', subtitle: 'Start a run of another automation (legacy v1 action).' },
};

// Explicit inputs for GENERIC automations. Never infer targets from the trigger.
export const ACTION_PERSON_INPUTS = {
    SEND_EMAIL: { key: 'recipientPersonIDs', label: 'Recipients', multiple: true },
    SEND_SMS: { key: 'recipientPersonIDs', label: 'Recipients', multiple: true },
    ENROLL_TRAINING: { key: 'personIDs', label: 'People to enroll', multiple: true },
    ADD_TO_SMALL_GROUP: { key: 'personIDs', label: 'People to add', multiple: true },
    ADD_TO_ROLE: { key: 'personIDs', label: 'People to add', multiple: true },
    INVITE_TO_REACH: { key: 'personIDs', label: 'People to invite', multiple: true },
    CHECK_INTO_EVENT: { key: 'personIDs', label: 'People to check in', multiple: true },
    UPDATE_PERSON: { key: 'personID', label: 'Person to update' },
    POST_TO_BOARD: { key: 'authorPersonID', label: 'Post author' },
};

export const PERSON_RECORD_FIELDS = [
    { key: 'id', label: 'Person ID', type: 'uuid', semantic: 'personID' },
    ...['firstName', 'lastName', 'fullName', 'email', 'phone'].map(key => ({
        key, label: ({ firstName: 'First name', lastName: 'Last name', fullName: 'Full name', email: 'Email', phone: 'Phone' })[key],
        type: 'string', nullable: true,
    })),
];

export const ACTION_TYPE_SECTIONS = [
    'Communication', 'People', 'Events & Training', 'Tasks & Approval',
    'Follow Up', 'Automations',
];

// Grouped SelectInput entries for a set of action-type values: section
// headers + separators, with a one-line subtitle on every option. Unknown
// values (stale/forward-compat actionTypes) land under 'Automations' so the
// dropdown can never silently drop the currently-selected type.
export const buildActionTypeOptions = (values = []) => {
    const allowed = new Set(values);
    const options = [];
    ACTION_TYPE_SECTIONS.forEach((section) => {
        const items = Object.entries(ACTION_TYPE_META)
            .filter(([value, meta]) => meta.section === section && allowed.has(value));
        if (!items.length) return;
        if (options.length) options.push({ separator: true });
        options.push({ section });
        items.forEach(([value, meta]) => {
            options.push({ value, label: meta.label, subtitle: meta.subtitle });
        });
    });
    return options;
};

// REQUEST_APPROVAL branches like a CONDITION but with a fixed outcome list —
// edges off the step carry branchID 'approved'|'denied' (worker contract).
export const APPROVAL_BRANCHES = [
    { id: 'approved', label: 'Approved' },
    { id: 'denied', label: 'Denied' },
];

// Steps that emit labelled branches: CONDITION via config.branches (+ 'default'
// No Match), REQUEST_APPROVAL via the fixed APPROVAL_BRANCHES list.
export const isApprovalStep = (step) =>
    step?.stepType === 'ACTION' && step?.actionType === 'REQUEST_APPROVAL';

export const isBranchSourceStep = (step) =>
    step?.stepType === 'CONDITION' || isApprovalStep(step);

// The branch defs a branch-source step exposes: {id, label, isDefault?}.
export const stepBranches = (step) => {
    if (isApprovalStep(step)) return APPROVAL_BRANCHES.map(b => ({ ...b }));
    if (step?.stepType === 'CONDITION') return step.config?.branches || [];
    return [];
};

// Step outputs each action type records on PathEnrollmentSteps.output
// (§14.7/§16.2 — bound downstream as steps.<stepID>.output.<field>).
export const ACTION_OUTPUT_FIELDS = {
    RESOLVE_PERSON: [
        { field: 'personID', label: 'Person ID', semantic: 'personID' },
        { field: 'resolution', label: 'Resolution' },
        { field: 'requiresReview', label: 'Requires Review', type: 'boolean' },
        { field: 'candidates', label: 'Candidate Count', type: 'number' },
    ],
    UPSERT_PERSON: [
        { field: 'personID', label: 'Person ID', semantic: 'personID' },
        { field: 'action', label: 'Outcome' },
    ],
    CREATE_TASK: [
        { field: 'taskInstanceID', label: 'Task Instance ID', semantic: 'taskInstanceID' },
        { field: 'taskID', label: 'Task ID', semantic: 'taskID' },
        { field: 'assigneePersonID', label: 'Assignee Person ID', semantic: 'personID' },
        { field: 'dueDate', label: 'Due Date', type: 'datetime' },
        { field: 'dueType', label: 'Due Type' },
    ],
    WAIT_FOR_TASK: [
        { field: 'taskInstanceID', label: 'Task Instance ID', semantic: 'taskInstanceID' },
        { field: 'outcome', label: 'Wait Outcome' },
    ],
    START_FOLLOW_UP_PATH: [
        { field: 'enrollmentID', label: 'Follow Up Enrollment ID', semantic: 'followUpEnrollmentID' },
    ],
    INVOKE_AUTOMATION: [
        { field: 'runID', label: 'Run ID', semantic: 'automationRunID' },
        { field: 'deduped', label: 'Was Deduped', type: 'boolean' },
    ],
    REQUEST_APPROVAL: [
        { field: 'taskInstanceID', label: 'Task Instance ID', semantic: 'taskInstanceID' },
        { field: 'outcome', label: 'Decision' },
    ],
};

// Declared output specs for one step: actionType-known outputs plus custom
// config.declaredOutputs entries ({key,label?,type?,semantic?}).
export const stepOutputSpecs = (step) => {
    if (!step) return [];
    const known = (ACTION_OUTPUT_FIELDS[step.actionType] || []).map(f => ({ ...f }));
    const declared = Array.isArray(step.config?.declaredOutputs) ? step.config.declaredOutputs : [];
    const seen = new Set(known.map(f => f.field));
    for (const d of declared) {
        const key = d?.key || d?.field;
        if (!key || seen.has(key)) continue;
        seen.add(key);
        known.push({ field: key, label: d.label || key, type: d.type, semantic: d.semantic });
    }
    return known;
};

const output = (field, type = 'string', semantic, extra = {}) => ({
    field, label: field.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()), type,
    ...(semantic ? { semantic } : {}), ...extra,
});
const peopleOutput = output('personIDs', 'list', 'peopleList', { itemSemantic: 'personID' });
const partialOutputs = [output('errors', 'list'), output('errorCount', 'number'), output('firstError', 'string', null, { nullable: true })];
Object.assign(ACTION_OUTPUT_FIELDS, {
    SEND_EMAIL: [output('outcome'), output('sentCount', 'number'), output('recipientPersonIDs', 'list', 'peopleList', { itemSemantic: 'personID' })],
    SEND_SMS: [output('outcome'), output('sentCount', 'number'), output('skippedCount', 'number'), output('recipientPersonIDs', 'list', 'peopleList', { itemSemantic: 'personID' })],
    ENROLL_TRAINING: [output('resourceID', 'uuid', 'trainingModuleID'), peopleOutput, output('enrolledCount', 'number'), output('alreadyEnrolledCount', 'number'), ...partialOutputs],
    ADD_TO_SMALL_GROUP: [output('resourceID', 'uuid', 'smallGroupID'), peopleOutput, output('addedCount', 'number'), output('alreadyMemberCount', 'number'), ...partialOutputs],
    ADD_TO_ROLE: [output('resourceID', 'uuid', 'roleID'), peopleOutput, output('addedCount', 'number'), output('alreadyInRoleCount', 'number'), ...partialOutputs],
    INVITE_TO_REACH: [peopleOutput, output('invitedCount', 'number'), ...partialOutputs],
    CHECK_INTO_EVENT: [output('resourceID', 'uuid', 'eventInstanceID'), peopleOutput, output('checkedInCount', 'number'), output('alreadyCheckedInCount', 'number'), ...partialOutputs],
    UPDATE_PERSON: [output('personID', 'uuid', 'personID'), output('updatedFields', 'list')],
    POST_TO_BOARD: [output('postID', 'uuid', 'postID'), output('boardID', 'uuid', 'boardID')],
    REGISTER_FOR_EVENT: [output('eventInstanceID', 'uuid', 'eventInstanceID'), output('groupID', 'uuid'), output('registered', 'number'), output('pending', 'number'), output('registrantPersonIDs', 'list', 'peopleList', { itemSemantic: 'personID' }), output('outcome')],
});
for (const action of ['RESOLVE_PERSON', 'UPSERT_PERSON']) {
    ACTION_OUTPUT_FIELDS[action].push(output('person', 'object', 'personRecord', { nullable: true, fields: PERSON_RECORD_FIELDS }));
    for (const spec of [output('resolution'), output('requiresReview', 'boolean')]) {
        if (!ACTION_OUTPUT_FIELDS[action].some(f => f.field === spec.field)) ACTION_OUTPUT_FIELDS[action].push(spec);
    }
}
for (const action of ['WAIT_FOR_TASK', 'REQUEST_APPROVAL']) {
    ACTION_OUTPUT_FIELDS[action].push(output('resolvedAt', 'datetime', null, { nullable: true }));
}
ACTION_OUTPUT_FIELDS.REQUEST_APPROVAL.push(output('decidedByPersonID', 'uuid', 'personID', { nullable: true }), output('decidedAt', 'datetime', null, { nullable: true }));
ACTION_OUTPUT_FIELDS.START_FOLLOW_UP_PATH.push(output('alreadyOpen', 'boolean'));

// ─── Guaranteed-upstream computation (mirror of the dominance check in
// automation_validate_bindings): step C is guaranteed upstream of target T
// iff T is NOT reachable from the trigger root without passing C. ────────────

const reachableWithout = (rootID, targetID, blockedID, edgeMap) => {
    if (!rootID || !targetID || rootID === targetID) return rootID === targetID;
    const seen = new Set();
    const stack = (edgeMap[rootID] || []).map(e => e.targetStepID).filter(id => id && id !== blockedID);
    while (stack.length) {
        const id = stack.pop();
        if (id === targetID) return true;
        if (seen.has(id)) continue;
        seen.add(id);
        for (const e of (edgeMap[id] || [])) {
            if (e.targetStepID && e.targetStepID !== blockedID) stack.push(e.targetStepID);
        }
    }
    return false;
};

export const buildEdgeMap = (edges = []) => {
    const map = {};
    for (const e of (edges || [])) {
        if (!e?.sourceStepID) continue;
        (map[e.sourceStepID] = map[e.sourceStepID] || []).push(e);
    }
    return map;
};

// IDs of steps guaranteed upstream of `targetID`. targetID may be null for a
// not-yet-saved step — pass anchorStepID (the source step the new step hangs
// off) so its own ancestors plus itself are returned.
export const computeGuaranteedUpstreamStepIDs = ({ steps = [], edges = [], targetID = null, anchorStepID = null }) => {
    const root = steps.find(s => s.stepType === 'TRIGGER');
    const edgeMap = buildEdgeMap(edges);
    const effectiveTarget = targetID || anchorStepID;
    if (!effectiveTarget) return [];
    const dominators = new Set();
    for (const candidate of steps) {
        if (candidate.stepType === 'TRIGGER') continue;
        if (candidate.id === effectiveTarget) continue;
        if (!reachableWithout(root?.id, effectiveTarget, candidate.id, edgeMap)) {
            dominators.add(candidate.id);
        }
    }
    // For a new step hanging off anchorStepID, the anchor itself is upstream.
    if (!targetID && anchorStepID && steps.some(s => s.id === anchorStepID && s.stepType !== 'TRIGGER')) {
        dominators.add(anchorStepID);
    }
    return steps.filter(s => dominators.has(s.id)).map(s => s.id);
};

// ─── Variable pickers ────────────────────────────────────────────────────────

// Variable list the v2 builder may offer: server registry vars (already
// source-aware; person.* never appears under GENERIC) plus synthesized
// steps.<id>.output.* vars for guaranteed-upstream steps.
export const buildV2VariableOptions = ({ registry, steps = [], upstreamStepIDs = [] }) => {
    const registryVars = Array.isArray(registry?.variables) ? registry.variables : [];
    const vars = registryVars.map(v => ({
        ...v,
        key: v.key,
        label: v.label || v.key,
        type: v.type || 'string',
        semantic: v.semantic || null,
        required: !!v.required,
        source: v.key?.startsWith('input.') ? 'input' : (v.key?.startsWith('run.') ? 'run' : 'trigger'),
    }));
    const upstreamSet = new Set(upstreamStepIDs || []);
    const positionOf = (id) => steps.find(s => s.id === id)?.position ?? 0;
    steps
        .filter(s => s.stepType !== 'TRIGGER' && upstreamSet.has(s.id))
        .sort((a, b) => positionOf(a.id) - positionOf(b.id))
        .forEach(step => {
            for (const spec of stepOutputSpecs(step)) {
                vars.push({
                    ...spec,
                    key: `steps.${step.id}.output.${spec.field}`,
                    label: `${step.name?.trim() || 'Step'} → ${spec.label || spec.field}`,
                    type: spec.type || (spec.semantic?.endsWith('ID') ? 'uuid' : 'string'),
                    semantic: spec.semantic || null,
                    source: 'step',
                    stepID: step.id,
                });
            }
        });
    return flattenAutomationVariables(vars);
};

export const flattenAutomationVariables = (variables = []) => {
    const result = new Map();
    const visit = (v, parent) => {
        const childKey = v.key || v.field;
        if (!childKey) return;
        const key = parent && !childKey.startsWith(`${parent.key}.`) ? `${parent.key}.${childKey}` : childKey;
        const spec = { ...v, key, label: parent ? `${parent.label} → ${v.label || childKey}` : (v.label || key),
            ...(parent ? { parentKey: parent.key, group: v.group || parent.group, source: parent.source, stepID: parent.stepID, nullable: !!(v.nullable || parent.nullable) } : {}) };
        result.set(key, { ...result.get(key), ...spec });
        const fields = v.fields || (v.semantic === 'personRecord' ? PERSON_RECORD_FIELDS : []);
        for (const child of Array.isArray(fields) ? fields : Object.entries(fields).map(([childKey, field]) => ({ key: childKey, ...field }))) visit(child, spec);
    };
    variables.forEach(v => visit(v));
    return [...result.values()];
};

export const variableCompatibilityReason = (v, param = {}) => {
    if (!param || (!param.semantic && !param.type && !param.text)) return null;
    const person = v.semantic === 'personID' || v.semantic === 'personRecord';
    const people = v.semantic === 'peopleList' || (v.type === 'list' && ['personRecord', 'personID', 'peopleList'].includes(v.itemSemantic));
    if (param.semantic === 'personID' || param.semantic === 'personRecord' || param.semantic === 'peopleList') {
        const acceptsList = param.multiple || param.semantic === 'peopleList';
        if (people) return acceptsList ? null : 'Choose one person, not a list of people.';
        if (person) return param.semantic === 'personRecord' && v.semantic !== 'personRecord' ? 'A person record is required.' : null;
        return 'A person or people variable is required.';
    }
    if (param.semantic && param.semantic !== v.semantic) return `Requires ${param.semantic}, not ${v.semantic || v.type || 'an untyped ID'}.`;
    if (param.text) return ['string', 'number', 'boolean', 'datetime', 'uuid'].includes(v.type) ? null : 'Records and lists cannot be inserted into text. Choose a child field.';
    if (param.type && param.type !== v.type) return `Requires ${param.type}, not ${v.type}.`;
    if (param.type === 'list' && param.itemSemantic && param.itemSemantic !== v.itemSemantic) return `Requires a list of ${param.itemSemantic}.`;
    return null;
};

export const friendlyVariableLabel = (variable) => {
    if (!variable) return 'Unavailable variable';
    return variable.label || variable.key;
};

export const filterVariablesForParam = (variables = [], paramDef = {}) => {
    const personParam = paramDef.semantic === 'personID' || paramDef.semantic === 'personRecord' || paramDef.semantic === 'peopleList';
    return variables.filter(v => {
        // A record's `.id` child is redundant when the record itself binds —
        // the worker coerces {id} at runtime, so show the person only.
        if (personParam && v.semantic === 'personID' && v.parentKey) return false;
        return !variableCompatibilityReason(v, paramDef);
    });
};

// ─── Action config builders (config shape per §14.6/§14.7 + worker contract) ──

export const isBinding = (v) => v && typeof v === 'object' && !Array.isArray(v) && typeof v.var === 'string';
const nonEmpty = (v) => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '');
// Person pickers emit {id, ...} objects; configs store the UUID or {var}.
const normPersonRef = (v) => (isBinding(v) ? v : (v?.id || v || undefined));

// Mixed people-target list → canonical [uuid|{var}] (deduped, blanks out).
export const normalizePersonTargets = (value) => {
    const raw = Array.isArray(value) ? value : (value === undefined || value === null ? [] : [value]);
    const out = [];
    for (const entry of raw) {
        const ref = normPersonRef(entry);
        if (!nonEmpty(ref) && !isBinding(ref)) continue;
        if (isBinding(ref) ? out.some(e => isBinding(e) && e.var === ref.var) : out.includes(ref)) continue;
        out.push(ref);
    }
    return out;
};

// Resource-picker keys kept per v2 legacy action alongside the explicit
// person target in ACTION_PERSON_INPUTS. Anything else (postAs,
// registrantSource, enrollee defaults) is v1-only state and never saved.
const V2_LEGACY_CONFIG_KEYS = {
    SEND_EMAIL: ['templateID'],
    SEND_SMS: ['message'],
    ENROLL_TRAINING: ['trainingModuleID'],
    ADD_TO_SMALL_GROUP: ['smallGroupID'],
    ADD_TO_ROLE: ['roleID'],
    INVITE_TO_REACH: [],
    UPDATE_PERSON: ['fields'],
    POST_TO_BOARD: ['boardID', 'title', 'content'],
    CHECK_INTO_EVENT: ['eventInstanceID'],
    REGISTER_FOR_EVENT: ['eventInstanceID', 'autoCreatePeople'],
};

// Build a v2 config for a legacy action: whitelisted resource keys plus the
// explicit people target. Bindings ({var}) pass through everywhere.
export const buildV2LegacyActionConfig = (actionType, raw = {}) => {
    const keep = V2_LEGACY_CONFIG_KEYS[actionType];
    if (!keep) return null;
    const config = {};
    for (const key of keep) {
        if (nonEmpty(raw[key])) config[key] = raw[key];
    }
    const target = ACTION_PERSON_INPUTS[actionType];
    if (target) {
        if (target.multiple) {
            const ids = normalizePersonTargets(raw[target.key]);
            if (ids.length) config[target.key] = ids;
        } else {
            const ref = normPersonRef(raw[target.key]);
            if (nonEmpty(ref) || isBinding(ref)) config[target.key] = ref;
        }
    }
    if (actionType === 'POST_TO_BOARD' && nonEmpty(raw.postAsPersonID) && !nonEmpty(config.authorPersonID) && !isBinding(config.authorPersonID)) {
        // 'Me' on a saved v1 config maps onto the explicit author slot.
        config.authorPersonID = raw.postAsPersonID;
    }
    if (actionType === 'REGISTER_FOR_EVENT') {
        const people = normalizePersonTargets(raw.registrantPersonIDs);
        if (people.length) config.registrantPersonIDs = people;
        if (nonEmpty(raw.registrants)) config.registrants = raw.registrants;
    }
    return config;
};

// Actions whose v2 config requires an explicit person/people target unless
// the run is LEGACY_PERSON compat (where the enrollee still fills it).
const v2TargetError = (actionType, rawConfig, compatibilityMode) => {
    if (compatibilityMode === 'LEGACY_PERSON') return null;
    if (actionType === 'REGISTER_FOR_EVENT') {
        return normalizePersonTargets(rawConfig.registrantPersonIDs).length || nonEmpty(rawConfig.registrants)
            ? null : 'Choose people to register or bind a registrant list.';
    }
    const target = ACTION_PERSON_INPUTS[actionType];
    if (!target) return null;
    if (target.multiple ? normalizePersonTargets(rawConfig[target.key]).length : nonEmpty(normPersonRef(rawConfig[target.key])) || isBinding(rawConfig[target.key])) {
        return null;
    }
    return `${target.label} requires at least one person or a bound variable.`;
};

// RESOLVE_PERSON: flat identity fields (worker toMatchInputs accepts
// firstName/lastName/email/phone or names[]/emails[]/phones[]).
export const buildResolvePersonConfig = (raw = {}) => {
    const config = {};
    for (const key of ['firstName', 'lastName', 'email', 'phone']) {
        if (nonEmpty(raw[key])) config[key] = raw[key];
    }
    return config;
};

// UPSERT_PERSON: {matchBy, createWhenUnmatched, updateWhenMatched, create, update}
export const buildUpsertPersonConfig = (raw = {}) => {
    const config = {};
    const matchBy = buildResolvePersonConfig(raw.matchBy || raw);
    if (Object.keys(matchBy).length) config.matchBy = matchBy;
    if (raw.createWhenUnmatched === true) {
        config.createWhenUnmatched = true;
        const create = {};
        for (const key of ['firstName', 'lastName', 'email', 'phone', 'dateOfBirth']) {
            if (nonEmpty(raw.create?.[key])) create[key] = raw.create[key];
        }
        if (Object.keys(create).length) config.create = create;
    }
    if (raw.updateWhenMatched === true) {
        config.updateWhenMatched = true;
        const update = {};
        for (const [k, v] of Object.entries(raw.update || {})) {
            if (nonEmpty(v)) update[k] = v;
        }
        if (Object.keys(update).length) config.update = update;
    }
    return config;
};

// CREATE_TASK: {taskID?, title, description, dueType, assigneePersonID?,
//   dueDate|dueIn{duration,unit}, followUp{personID,purpose,followUpEnrollmentID?,safeLogistics?}}
export const buildCreateTaskConfig = (raw = {}) => {
    const config = {};
    const taskID = raw.taskID || raw.task?.id;
    if (nonEmpty(taskID)) config.taskID = taskID;
    if (nonEmpty(raw.title)) config.title = raw.title;
    if (nonEmpty(raw.description)) config.description = raw.description;
    config.dueType = raw.dueType || 'AUTOMATIONS';
    const assignee = normPersonRef(raw.assigneePersonID);
    if (nonEmpty(assignee)) config.assigneePersonID = assignee;
    const assignees = normalizePersonTargets(raw.assigneePersonIDs);
    if (assignees.length) config.assigneePersonIDs = assignees;
    if (raw.dueMode === 'ABSOLUTE' && nonEmpty(raw.dueDate)) {
        config.dueDate = raw.dueDate;
    } else {
        const duration = parseInt(raw.dueIn?.duration);
        if (Number.isInteger(duration) && duration > 0) {
            config.dueIn = { duration, unit: raw.dueIn?.unit || 'days' };
        }
    }
    if (config.dueType === 'FOLLOW_UP' && nonEmpty(normPersonRef(raw.followUp?.personID))) {
        const fu = { personID: normPersonRef(raw.followUp.personID), purpose: raw.followUp.purpose || 'OTHER' };
        if (nonEmpty(raw.followUp.followUpEnrollmentID)) fu.followUpEnrollmentID = raw.followUp.followUpEnrollmentID;
        if (nonEmpty(raw.followUp.safeLogistics)) fu.safeLogistics = raw.followUp.safeLogistics;
        config.followUp = fu;
    }
    return config;
};

// WAIT_FOR_TASK: {taskInstanceID, deadlineAt|timeoutDuration+timeoutUnit}
export const buildWaitForTaskConfig = (raw = {}) => {
    const config = {};
    if (nonEmpty(raw.taskInstanceID)) config.taskInstanceID = raw.taskInstanceID;
    if (raw.deadlineMode === 'ABSOLUTE' && nonEmpty(raw.deadlineAt)) {
        config.deadlineAt = raw.deadlineAt;
    } else {
        const duration = parseInt(raw.timeoutDuration);
        if (Number.isInteger(duration) && duration > 0) {
            config.timeoutDuration = duration;
            config.timeoutUnit = raw.timeoutUnit || 'days';
        }
    }
    return config;
};

// START_FOLLOW_UP_PATH: {personID, followUpPathID}
export const buildStartFollowUpPathConfig = (raw = {}) => {
    const config = {};
    const person = normPersonRef(raw.personID);
    if (nonEmpty(person)) config.personID = person;
    if (nonEmpty(raw.followUpPathID)) config.followUpPathID = raw.followUpPathID;
    return config;
};

// INVOKE_AUTOMATION: {automationID, input{}, personID?}
export const buildInvokeAutomationConfig = (raw = {}) => {
    const config = {};
    if (nonEmpty(raw.automationID)) config.automationID = raw.automationID;
    const person = normPersonRef(raw.personID);
    if (nonEmpty(person)) config.personID = person;
    const input = {};
    for (const [k, v] of Object.entries(raw.input || {})) {
        // Person-record literals collapse to their id — a picked person is
        // an explicit reference; object/list declared inputs stay objects.
        const val = v && typeof v === 'object' && !isBinding(v) && v.id ? v.id : v;
        if (nonEmpty(val)) input[k] = val;
    }
    if (Object.keys(input).length) config.input = input;
    return config;
};

// REQUEST_APPROVAL: {title?, request?, approverPersonIDs[], dueDate|dueIn{duration,unit}}
// approverPersonIDs accepts the multi-person picker's person objects, raw
// uuids, and {var} bindings — each entry normalizes independently.
export const buildRequestApprovalConfig = (raw = {}) => {
    const config = {};
    if (nonEmpty(raw.title)) config.title = raw.title;
    if (nonEmpty(raw.request)) config.request = raw.request;
    const approvers = (Array.isArray(raw.approverPersonIDs) ? raw.approverPersonIDs : [])
        .map(normPersonRef)
        .filter(nonEmpty);
    if (approvers.length) config.approverPersonIDs = approvers;
    if (raw.dueMode === 'ABSOLUTE' && nonEmpty(raw.dueDate)) {
        config.dueDate = raw.dueDate;
    } else {
        const duration = parseInt(raw.dueIn?.duration);
        if (Number.isInteger(duration) && duration > 0) {
            config.dueIn = { duration, unit: raw.dueIn?.unit || 'days' };
        }
    }
    return config;
};

// Cheap client-side "is this step ready to save" checks. Server-side
// automation_validate_bindings is authoritative — these just keep obviously
// empty required params from round-tripping.
export const validateV2ActionDraft = (actionType, rawConfig = {}, opts = {}) => {
    const errors = [];
    const targetError = v2TargetError(actionType, rawConfig, opts.compatibilityMode);
    if (targetError) errors.push(targetError);
    switch (actionType) {
        case 'RESOLVE_PERSON': {
            const cfg = buildResolvePersonConfig(rawConfig);
            if (!Object.keys(cfg).length) errors.push('Provide at least one identity field (name, email, or phone) to match on.');
            break;
        }
        case 'UPSERT_PERSON': {
            const cfg = buildUpsertPersonConfig(rawConfig);
            if (!Object.keys(cfg.matchBy || {}).length) errors.push('Provide at least one match field (name, email, or phone).');
            break;
        }
        case 'CREATE_TASK':
            if (rawConfig.dueType === 'FOLLOW_UP' && !nonEmpty(rawConfig.followUp?.personID)) {
                errors.push('Follow Up tasks require a person target.');
            }
            break;
        case 'WAIT_FOR_TASK':
            if (!nonEmpty(rawConfig.taskInstanceID)) errors.push('Bind a task instance to wait on (usually an upstream Create Task output).');
            break;
        case 'START_FOLLOW_UP_PATH':
            if (!nonEmpty(rawConfig.personID)) errors.push('Choose who to enroll (a person variable or specific person).');
            if (!nonEmpty(rawConfig.followUpPathID)) errors.push('Select a Follow Up path.');
            break;
        case 'INVOKE_AUTOMATION':
            if (!nonEmpty(rawConfig.automationID)) errors.push('Select an automation to invoke.');
            break;
        case 'REQUEST_APPROVAL': {
            const cfg = buildRequestApprovalConfig(rawConfig);
            if (!cfg.approverPersonIDs?.length) errors.push('Choose at least one approver.');
            if (!nonEmpty(rawConfig.title) && !nonEmpty(rawConfig.request)) {
                errors.push('Give approvers a title or request text so they know what they are deciding.');
            }
            break;
        }
        default:
            break;
    }
    return errors;
};

// ─── Publish payload + result rendering (§15.4) ─────────────────────────────

// expectedRevision is REQUIRED (stale → AUTOMATION_CONFLICT). Only send
// fields the caller actually wants changed.
export const buildPublishPayload = ({ path, name, description, isActive, inputSchema } = {}) => {
    const payload = { expectedRevision: path?.revision };
    if (name !== undefined) payload.name = name;
    if (description !== undefined) payload.description = description;
    if (isActive !== undefined) payload.isActive = isActive;
    if (inputSchema !== undefined) payload.inputSchema = inputSchema;
    return payload;
};

// inputSchema shape: {fields: [{key,label?,type,semantic?,required?}]}
export const buildInputSchema = (fields = []) => ({
    fields: (fields || [])
        .filter(f => nonEmpty(f?.key))
        .map(f => ({
            key: f.key,
            ...(nonEmpty(f.label) ? { label: f.label } : {}),
            type: f.type || 'string',
            ...(nonEmpty(f.semantic) ? { semantic: f.semantic } : {}),
            required: f.required === true,
        })),
});

// automation_start_run input shape: {personID?, input?, event?}
export const buildStartRunInput = ({ personID, input } = {}) => {
    const payload = {};
    if (personID) payload.personID = personID;
    payload.input = input && typeof input === 'object' ? input : {};
    return payload;
};

// Honest §15.4 impact/reconcile summary lines for the publish result sheet.
export const describePublishResult = (result = {}) => {
    const impact = result.impact || {};
    const rec = result.reconcile || {};
    const lines = [];
    lines.push(`Published revision ${result.revision ?? '?'}.`);
    if (impact.activeRuns) lines.push(`${impact.activeRuns} active run${impact.activeRuns === 1 ? '' : 's'} on this automation.`);
    if (rec.restamped) lines.push(`${rec.restamped} queued step${rec.restamped === 1 ? '' : 's'} updated to the new version.`);
    if (rec.resumedFromReview) lines.push(`${rec.resumedFromReview} parked step${rec.resumedFromReview === 1 ? '' : 's'} resumed on the new version.`);
    if (rec.waitsRetained || impact.pendingTaskWaits) {
        const n = rec.waitsRetained ?? impact.pendingTaskWaits;
        lines.push(`${n} task wait${n === 1 ? '' : 's'} kept running (task identity preserved).`);
    }
    const parked = rec.parked ?? impact.parkedForReview ?? 0;
    if (parked) lines.push(`${parked} step${parked === 1 ? '' : 's'} parked for review — open the run to continue it manually.`);
    if (rec.inFlight) lines.push(`${rec.inFlight} step${rec.inFlight === 1 ? '' : 's'} still in progress — untouched by this publish.`);
    if (!impact.activeRuns) lines.push('No active runs were affected.');
    return lines;
};

const CONFLICT_REASON_LABELS = {
    STEP_REMOVED: 'A step was removed while runs had it pending — it is parked for review.',
    STEP_REMOVED_WITH_TASK_WAIT: 'A removed step had an open task wait — the task keeps its identity and waits for review.',
    PRE_EXECUTION_STALE_REVISION: 'A pending step still references an older version — it is flagged for review.',
};

export const describePublishConflicts = (result = {}) => {
    const conflicts = result?.reconcile?.conflicts || [];
    return conflicts.map(c => ({
        ...c,
        label: CONFLICT_REASON_LABELS[c.reason] || `Step needs review (${c.reason || 'unknown reason'}).`,
    }));
};

// ─── Error contract ──────────────────────────────────────────────────────────
// automation_* RPCs use PostgREST DETAIL codes (§16.2): 42501
// AUTOMATION_DENIED, 22023 AUTOMATION_INVALID, 40001 AUTOMATION_CONFLICT.
// Validation failures carry 'AUTOMATION_INVALID: [{...errors}]' in DETAIL.

export const extractAutomationCode = (error) => {
    if (!error) return null;
    if (typeof error.details === 'string' && error.details.startsWith('AUTOMATION_')) return error.details.split(/[:\s]/)[0];
    const match = `${error.details ?? ''} ${error.message ?? ''}`.match(/AUTOMATION_[A-Z_]+/);
    return match?.[0] ?? null;
};

export const extractAutomationErrors = (error) => {
    const detail = typeof error?.details === 'string' ? error.details : '';
    const match = detail.match(/AUTOMATION_INVALID:\s*(\[.*\])\s*$/s);
    if (!match) return [];
    try {
        const parsed = JSON.parse(match[1]);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

export const isAutomationConflict = (error) => extractAutomationCode(error) === 'AUTOMATION_CONFLICT';

// ─── Run detail: waitState + captured input (P9, §14.7/§14.9/§15.3/A22) ──────

export const WAIT_OUTCOME_LABELS = {
    PENDING: 'Pending',
    COMPLETED: 'Completed',
    CANCELLED: 'Cancelled',
    TIMED_OUT: 'Timed out',
    FAILED: 'Failed',
};

const formatWaitTimestamp = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('default', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Maps PathEnrollmentSteps.waitState (§14.7/§15.4 worker contract) to an
// honest display descriptor the run detail renders. resolution tells the
// screen which affordance is safe:
//   'RESUME'        — parked REVISION_REVIEW/step-review: re-enqueue under
//                     the current revision (mirrors _automation_publish_sweep).
//   'CONTINUE_PAST' — the step no longer exists (STEP_REMOVED): the only
//                     honest resolution is advancing past it.
//   'RETRY'         — PROVIDER_UNCERTAIN: retry_path_step (FAILED → PENDING)
//                     after the operator reviews for a possible duplicate.
//   null            — informational only (task waits, resolved parks).
export const describeStepWaitState = (enrollmentStep, { steps = [] } = {}) => {
    const wait = enrollmentStep?.waitState;
    if (!wait || typeof wait !== 'object') return null;
    const kind = wait.kind;
    if (!kind) return null;
    const stepName = steps.find(s => s.id === enrollmentStep.pathStepID)?.name
        || enrollmentStep?.PathStep?.name || 'this step';

    if (kind === 'TASK') {
        const outcome = wait.outcome || 'PENDING';
        if (outcome === 'PENDING') {
            return {
                kind: 'TASK',
                tone: 'warning',
                taskInstanceID: wait.taskInstanceID || null,
                title: `Waiting on task — ${stepName}`,
                detail: wait.deadlineAt
                    ? `The run resumes when the task finishes or the deadline passes (${formatWaitTimestamp(wait.deadlineAt)}).`
                    : 'The run resumes when the task finishes. No deadline.',
                resolution: null,
            };
        }
        return {
            kind: 'TASK',
            tone: outcome === 'COMPLETED' ? 'success' : 'warning',
            taskInstanceID: wait.taskInstanceID || null,
            title: `Task wait ${WAIT_OUTCOME_LABELS[outcome]?.toLowerCase() || outcome.toLowerCase()}`,
            detail: wait.resolvedAt ? `Resolved ${formatWaitTimestamp(wait.resolvedAt)}.` : null,
            resolution: null,
        };
    }

    if (kind === 'REVISION_REVIEW') {
        const removed = wait.reason === 'STEP_REMOVED';
        const queued = wait.queuedRevision != null ? `revision ${wait.queuedRevision}` : 'an older revision';
        const current = wait.currentRevision != null ? `revision ${wait.currentRevision}` : 'the current revision';
        return {
            kind: 'REVISION_REVIEW',
            tone: 'warning',
            reason: wait.reason || null,
            title: removed
                ? `Step removed — ${stepName}`
                : `Step paused — ${stepName}`,
            detail: removed
                ? 'This step was removed from the automation while the run had it pending. Its history is kept; continue past it when you are done reviewing.'
                : `This step was queued under ${queued} and the definition is now on ${current}. Resume it to run the latest published config.`,
            resolution: removed ? 'CONTINUE_PAST' : 'RESUME',
        };
    }

    if (kind === 'PROVIDER_UNCERTAIN') {
        return {
            kind: 'PROVIDER_UNCERTAIN',
            tone: 'error',
            title: `Outcome unknown — ${stepName}`,
            detail: 'The send may or may not have reached the provider. Review for a possible duplicate before retrying.',
            resolution: 'RETRY',
        };
    }

    // Defensive: other parked review kinds (STEP_REVIEW, STEP_REMOVED and any
    // future kind) surface honestly rather than looking like a normal wait.
    const removed = kind === 'STEP_REMOVED' || wait.reason === 'STEP_REMOVED';
    return {
        kind,
        tone: 'warning',
        reason: wait.reason || null,
        title: `Step paused for review — ${stepName}`,
        detail: wait.detail || wait.reason || 'This step needs review before the run can continue.',
        resolution: removed ? 'CONTINUE_PAST' : (kind === 'STEP_REVIEW' ? 'RESUME' : null),
    };
};

// True when an enrollment step is parked awaiting human review
// (waitState outcome REQUIRES_REVIEW on any parked kind).
export const isStepParkedForReview = (enrollmentStep) =>
    enrollmentStep?.waitState?.outcome === 'REQUIRES_REVIEW';

const formatCapturedValue = (v) => {
    if (v === true) return 'Yes';
    if (v === false) return 'No';
    if (v === null || v === undefined) return '—';
    if (typeof v === 'object') {
        try { return JSON.stringify(v); } catch { return String(v); }
    }
    return String(v);
};

// The captured input/event recorded at run start (§15.3/A22: immutable
// historical fact — displayed verbatim, never re-resolved against the live
// definition). inputSchema labels are applied when they still match.
export const describeCapturedInput = (enrollment, { inputSchema } = {}) => {
    const captured = enrollment?.capturedInput;
    if (!captured || typeof captured !== 'object' || Array.isArray(captured)) {
        return { fields: [], event: null, hasCapture: false };
    }
    const declared = (captured.input && typeof captured.input === 'object' && !Array.isArray(captured.input))
        ? captured.input
        : {};
    const fieldSpecs = Array.isArray(inputSchema?.fields) ? inputSchema.fields : [];
    const labelFor = (key) => fieldSpecs.find(f => f?.key === key)?.label || key;
    const fields = Object.entries(declared)
        .filter(([key]) => key !== 'dedupeKey')
        .map(([key, value]) => ({ key, label: labelFor(key), value: formatCapturedValue(value) }));
    if (captured.personID) {
        const name = enrollment?.Person
            ? `${enrollment.Person.firstName || ''} ${enrollment.Person.lastName || ''}`.trim()
            : null;
        fields.unshift({ key: 'personID', label: 'Run person', value: name || 'Linked person' });
    }
    const evt = (captured.event && typeof captured.event === 'object') ? captured.event : null;
    const event = (evt || enrollment.capturedEventType) ? {
        type: evt?.type || enrollment.capturedEventType || null,
        sourceType: evt?.sourceType || enrollment.capturedEventSourceType || null,
        sourceID: evt?.sourceID || enrollment.capturedEventSourceID || null,
        occurredAt: evt?.occurredAt || null,
        dedupeKey: evt?.dedupeKey || captured.dedupeKey || enrollment.runDedupeKey || null,
    } : null;
    return { fields, event, hasCapture: fields.length > 0 || !!event };
};

// Safe step outputs: scalar values only (objects are flattened one level as
// JSON so a malformed payload never renders "[object Object]" or crashes).
export const describeStepOutput = (enrollmentStep, { steps = [] } = {}) => {
    const output = enrollmentStep?.output;
    if (!output || typeof output !== 'object' || Array.isArray(output)) return [];
    const step = steps.find(s => s.id === enrollmentStep.pathStepID);
    const specs = stepOutputSpecs(step);
    const labelFor = (field) => specs.find(s => s.field === field)?.label || field;
    return Object.entries(output)
        .filter(([, v]) => v !== undefined)
        .map(([field, value]) => ({ field, label: labelFor(field), value: formatCapturedValue(value) }));
};

// ─── STEP_OUTPUT condition rule (v2 defs only; §14.5 wait-outcome branching) ──
// Worker contract: {ruleType:'STEP_OUTPUT', key:'<pathStepID>.<field>',
// operator, value}. Operators mirror evaluateStepOutputRule in
// _shared/automationRuntime.ts.

export const STEP_OUTPUT_OPERATORS = ['equals', 'not_equals', 'contains', 'not_contains', 'is_empty', 'is_not_empty'];

export const STEP_OUTPUT_OPERATOR_OPTIONS = STEP_OUTPUT_OPERATORS.map(op => ({ value: op, label: op }));

// Step+field options for a STEP_OUTPUT condition key. Only guaranteed-upstream
// steps may be offered (same dominance rule as the variable pickers).
export const buildStepOutputRuleOptions = ({ steps = [], upstreamStepIDs = [] }) => {
    const upstream = new Set(upstreamStepIDs || []);
    return (steps || [])
        .filter(s => s.stepType !== 'TRIGGER' && upstream.has(s.id))
        .flatMap(step => stepOutputSpecs(step).map(spec => ({
            value: `${step.id}.${spec.field}`,
            label: `${step.name?.trim() || 'Step'} → ${spec.label || spec.field}`,
            stepID: step.id,
            field: spec.field,
        })));
};

// '<pathStepID>.<field>' → {stepID, field}. Malformed keys parse as nulls.
export const parseStepOutputRuleKey = (key) => {
    if (typeof key !== 'string') return { stepID: null, field: null };
    const dot = key.indexOf('.');
    if (dot <= 0) return { stepID: null, field: null };
    return { stepID: key.slice(0, dot), field: key.slice(dot + 1) };
};

// ─── Semantic ID separation (§14.5): FollowUpPath pickers never blur with
// legacy Paths pickers. §25.12: there is no Path kind — every Follow Up
// template is a plain Path. ────

export const followUpPathLabel = (path) => path?.name || 'Follow Up path';

export const buildFollowUpPathOptions = (paths = []) =>
    (paths || [])
        .filter(p => p && !p.isArchived)
        .map(p => ({ value: p.pathID, label: followUpPathLabel(p) }));

// ─── Start-run draft (v2 MANUAL / PATH_ACTION declared inputs, §16.1) ─────────

// Declared inputSchema fields a start-run form must collect.
export const declaredInputFields = (path) =>
    (Array.isArray(path?.inputSchema?.fields) ? path.inputSchema.fields : [])
        .filter(f => f?.key);

// Person need for starting a run: the server contract wins
// (automation_get_variables personOptional), then the trigger catalog row.
// GENERIC defs are person-optional by design — a run never invents a fake
// person, and the picker stays optional unless the contract requires one.
export const runStartPersonRequirement = ({ registry, catalogRow } = {}) => {
    const optional = registry?.personOptional ?? catalogRow?.personOptional;
    return optional === false ? 'required' : 'optional';
};

// Coerce draft values to each field's declared type. Empty/absent values are
// omitted so server-side inputSchema validation stays the source of truth.
export const buildDeclaredInputValues = ({ fields = [], draft = {} } = {}) => {
    const input = {};
    for (const field of (fields || [])) {
        if (!field?.key) continue;
        const raw = draft[field.key];
        if (raw === undefined || raw === null || raw === '') continue;
        switch (field.type) {
            case 'number': {
                const n = Number(raw);
                if (!Number.isNaN(n)) input[field.key] = n;
                break;
            }
            case 'boolean':
                input[field.key] = raw === true || raw === 'true';
                break;
            default:
                input[field.key] = typeof raw === 'object' && raw?.id ? raw.id : raw;
        }
    }
    return input;
};

// Missing required-field keys for the start-run form (client-side hint only —
// automation_start_run re-validates server-side).
export const missingRequiredInputKeys = ({ fields = [], draft = {} } = {}) =>
    (fields || [])
        .filter(f => f?.key && f.required === true)
        .filter(f => draft[f.key] === undefined || draft[f.key] === null || draft[f.key] === '')
        .map(f => f.key);
